import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import ImageUpload from "@/components/ImageUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";
import api, { apiErr, fileUrl } from "@/lib/api";
import { Shield, Users, Link2, LayoutTemplate, MousePointerClick, Trash2, Plus, Loader2, Zap, LogOut, Map } from "lucide-react";

function AdminLogin() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.user.role !== "admin") { toast.error("Bu hesap admin değil"); setLoading(false); return; }
      setUser(data.user);
      toast.success("Admin girişi başarılı");
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 px-4">
      <Card className="w-full max-w-sm p-8 rounded-2xl">
        <div className="flex items-center gap-2 justify-center mb-6">
          <span className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center"><Shield className="w-5 h-5 text-white" /></span>
          <span className="font-display text-lg font-extrabold">Admin Panel</span>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div><Label className="mb-1.5 block">E-posta</Label><Input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" /></div>
          <div><Label className="mb-1.5 block">Şifre</Label><Input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-11" /></div>
          <Button data-testid="admin-login-button" type="submit" disabled={loading} className="w-full h-11 bg-blue-600 hover:bg-blue-700">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Giriş Yap"}</Button>
        </form>
        <button onClick={() => navigate("/")} className="text-xs text-slate-400 mt-4 w-full text-center">← Ana sayfa</button>
      </Card>
    </div>
  );
}

export default function Admin() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [links, setLinks] = useState([]);
  const [sites, setSites] = useState([]);
  const [ads, setAds] = useState([]);
  const [settings, setSettings] = useState({ site_name: "", site_description: "", logo_url: "", favicon_url: "", countdown_seconds: 4 });
  const [newAd, setNewAd] = useState({ image_url: "", target_url: "", title: "", is_active: true });

  useEffect(() => { document.title = "Admin Panel — kisalink.net"; }, []);

  const loadAll = useCallback(async () => {
    try {
      const [s, u, l, m, a, st] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/users"), api.get("/admin/links"),
        api.get("/admin/minisites"), api.get("/admin/ads"), api.get("/admin/settings"),
      ]);
      setStats(s.data); setUsers(u.data); setLinks(l.data); setSites(m.data); setAds(a.data); setSettings(st.data);
    } catch { toast.error("Admin verileri yüklenemedi"); }
  }, []);

  useEffect(() => { if (user && user.role === "admin") loadAll(); }, [user, loadAll]);

  if (authLoading || user === null) return <div className="min-h-screen flex items-center justify-center bg-slate-900"><Loader2 className="w-7 h-7 animate-spin text-blue-500" /></div>;
  if (!user || user.role !== "admin") return <AdminLogin />;

  const setUserField = async (id, patch) => { try { await api.put(`/admin/users/${id}`, patch); loadAll(); } catch { toast.error("Güncellenemedi"); } };
  const delUser = async (id) => { try { await api.delete(`/admin/users/${id}`); toast.success("Kullanıcı silindi"); loadAll(); } catch { toast.error("Silinemedi"); } };
  const toggleLink = async (l) => { try { await api.put(`/admin/links/${l.id}`, { is_active: !l.is_active }); loadAll(); } catch {} };
  const delLink = async (id) => { try { await api.delete(`/admin/links/${id}`); toast.success("Link silindi"); loadAll(); } catch {} };
  const delSite = async (id) => { try { await api.delete(`/admin/minisites/${id}`); toast.success("Sayfa silindi"); loadAll(); } catch {} };
  const toggleSite = async (s) => { try { await api.put(`/admin/minisites/${s.id}`, { is_active: !s.is_active }); loadAll(); } catch {} };

  const createAd = async () => {
    if (!newAd.image_url && !newAd.title) return toast.error("Görsel veya başlık gerekli");
    try { await api.post("/admin/ads", newAd); toast.success("Reklam eklendi"); setNewAd({ image_url: "", target_url: "", title: "", is_active: true }); loadAll(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const toggleAd = async (ad) => { try { await api.put(`/admin/ads/${ad.id}`, { ...ad, is_active: !ad.is_active }); loadAll(); } catch {} };
  const delAd = async (id) => { try { await api.delete(`/admin/ads/${id}`); toast.success("Reklam silindi"); loadAll(); } catch {} };

  const saveSettings = async () => { try { await api.put("/admin/settings", settings); toast.success("Ayarlar kaydedildi"); loadAll(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };

  const statCards = stats ? [
    { label: "Toplam Kullanıcı", value: stats.total_users, icon: Users },
    { label: "Toplam Link", value: stats.total_links, icon: Link2 },
    { label: "Mini Siteler", value: stats.total_sites, icon: LayoutTemplate },
    { label: "Toplam Tıklama", value: stats.total_clicks, icon: MousePointerClick },
  ] : [];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="h-16 bg-slate-900 flex items-center px-4 sm:px-6 sticky top-0 z-30">
        <span className="flex items-center gap-2 text-white font-display font-extrabold"><span className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center"><Shield className="w-4 h-4 text-white" /></span>Admin Panel</span>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-slate-300 text-sm hidden sm:block">{user.email}</span>
          <Button size="sm" variant="ghost" className="text-slate-300 hover:text-white" onClick={() => navigate("/")}><Zap className="w-4 h-4" /></Button>
          <Button size="sm" variant="ghost" className="text-slate-300 hover:text-white" onClick={async () => { await logout(); navigate("/"); }} data-testid="admin-logout"><LogOut className="w-4 h-4" /></Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <Tabs defaultValue="dashboard">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="dashboard" data-testid="admin-tab-dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users">Kullanıcılar</TabsTrigger>
            <TabsTrigger value="links" data-testid="admin-tab-links">Linkler</TabsTrigger>
            <TabsTrigger value="sites" data-testid="admin-tab-sites">Mini Siteler</TabsTrigger>
            <TabsTrigger value="ads" data-testid="admin-tab-ads">Reklamlar</TabsTrigger>
            <TabsTrigger value="settings" data-testid="admin-tab-settings">Ayarlar</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="mt-6 space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {statCards.map((c, i) => (
                <Card key={i} className="p-5 rounded-2xl" data-testid={`admin-stat-${i}`}>
                  <c.icon className="w-5 h-5 text-blue-600" />
                  <p className="mt-3 text-2xl font-extrabold font-display">{c.value}</p>
                  <p className="text-sm text-slate-500">{c.label}</p>
                </Card>
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Card className="p-5 rounded-2xl"><p className="text-sm text-slate-500">Günlük Tıklama</p><p className="text-3xl font-extrabold font-display mt-1">{stats?.daily_clicks ?? 0}</p></Card>
              <Card className="p-5 rounded-2xl"><p className="text-sm text-slate-500">Haftalık Tıklama</p><p className="text-3xl font-extrabold font-display mt-1">{stats?.weekly_clicks ?? 0}</p></Card>
              <Card className="p-5 rounded-2xl"><p className="text-sm text-slate-500">Aylık Tıklama</p><p className="text-3xl font-extrabold font-display mt-1">{stats?.monthly_clicks ?? 0}</p></Card>
            </div>
            <Card className="p-5 rounded-2xl">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold">Tıklanma Trendi (14 gün)</h2>
                <a href="/api/sitemap.xml" target="_blank" rel="noreferrer" className="text-xs text-blue-600 flex items-center gap-1"><Map className="w-3.5 h-3.5" /> Sitemap ({stats?.sitemap_entries ?? 0} kayıt)</a>
              </div>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={stats?.series || []}>
                    <defs><linearGradient id="ag" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2563EB" stopOpacity={0.35} /><stop offset="100%" stopColor="#2563EB" stopOpacity={0} /></linearGradient></defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(d) => d.slice(5)} />
                    <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} allowDecimals={false} width={28} />
                    <Tooltip />
                    <Area type="monotone" dataKey="clicks" stroke="#2563EB" strokeWidth={2} fill="url(#ag)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="users" className="mt-6">
            <Card className="rounded-2xl overflow-x-auto">
              <Table data-testid="admin-users-table">
                <TableHeader><TableRow><TableHead>E-posta</TableHead><TableHead>İsim</TableHead><TableHead>Rol</TableHead><TableHead>Durum</TableHead><TableHead>Link</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} data-testid={`admin-user-${u.id}`}>
                      <TableCell className="font-medium">{u.email}</TableCell>
                      <TableCell>{u.name}</TableCell>
                      <TableCell><Badge variant={u.role === "admin" ? "default" : "secondary"} className={u.role === "admin" ? "bg-blue-600" : ""}>{u.role}</Badge></TableCell>
                      <TableCell>
                        <Switch checked={u.status !== "banned"} onCheckedChange={(v) => setUserField(u.id, { status: v ? "active" : "banned" })} data-testid={`ban-toggle-${u.id}`} />
                      </TableCell>
                      <TableCell>{u.link_count}</TableCell>
                      <TableCell className="text-right">
                        {u.id !== user.id && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="text-red-500"><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                            <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Kullanıcı silinsin mi?</AlertDialogTitle><AlertDialogDescription>Tüm link ve sayfaları da silinir.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>İptal</AlertDialogCancel><AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => delUser(u.id)}>Sil</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                          </AlertDialog>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="links" className="mt-6">
            <Card className="rounded-2xl overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Kod</TableHead><TableHead>Hedef</TableHead><TableHead>Tık</TableHead><TableHead>Aktif</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {links.map((l) => (
                    <TableRow key={l.id}>
                      <TableCell className="font-mono text-blue-700">/{l.code}</TableCell>
                      <TableCell className="max-w-[240px] truncate">{l.target_url}</TableCell>
                      <TableCell>{l.clicks}</TableCell>
                      <TableCell><Switch checked={l.is_active} onCheckedChange={() => toggleLink(l)} data-testid={`admin-link-toggle-${l.code}`} /></TableCell>
                      <TableCell className="text-right">
                        <AlertDialog>
                          <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="text-red-500" data-testid={`admin-link-delete-${l.code}`}><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                          <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Link silinsin mi?</AlertDialogTitle></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>İptal</AlertDialogCancel><AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => delLink(l.id)}>Sil</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="sites" className="mt-6">
            <Card className="rounded-2xl overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Başlık</TableHead><TableHead>Slug</TableHead><TableHead>Tip</TableHead><TableHead>Görüntülenme</TableHead><TableHead>Yayında</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {sites.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell className="font-medium">{s.title}</TableCell>
                      <TableCell className="font-mono text-sm">/{s.slug}</TableCell>
                      <TableCell><Badge variant="secondary">{s.mode === "html" ? "HTML" : "Şablon"}</Badge></TableCell>
                      <TableCell>{s.views}</TableCell>
                      <TableCell><Switch checked={s.is_active} onCheckedChange={() => toggleSite(s)} /></TableCell>
                      <TableCell className="text-right">
                        <AlertDialog>
                          <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="text-red-500"><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                          <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Sayfa silinsin mi?</AlertDialogTitle></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>İptal</AlertDialogCancel><AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => delSite(s.id)}>Sil</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="ads" className="mt-6 space-y-6">
            <Card className="p-6 rounded-2xl">
              <h2 className="font-semibold mb-4">Yeni Reklam Ekle</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label className="mb-1.5 block">Reklam Görseli</Label><ImageUpload value={newAd.image_url} onChange={(v) => setNewAd({ ...newAd, image_url: v })} testid="ad-image" label="Reklam" /></div>
                <div className="space-y-3">
                  <div><Label className="mb-1.5 block">Reklam URL'si</Label><Input data-testid="ad-target-input" value={newAd.target_url} onChange={(e) => setNewAd({ ...newAd, target_url: e.target.value })} placeholder="https://..." /></div>
                  <div><Label className="mb-1.5 block">Başlık (opsiyonel)</Label><Input value={newAd.title} onChange={(e) => setNewAd({ ...newAd, title: e.target.value })} /></div>
                </div>
              </div>
              <Button className="mt-4 bg-blue-600 hover:bg-blue-700" onClick={createAd} data-testid="ad-create-button"><Plus className="w-4 h-4 mr-1" /> Reklam Ekle</Button>
            </Card>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {ads.length === 0 && <div className="col-span-full text-center text-slate-400 py-8">Henüz reklam yok.</div>}
              {ads.map((ad) => (
                <Card key={ad.id} className="p-4 rounded-2xl" data-testid={`ad-card-${ad.id}`}>
                  {ad.image_url ? <img src={fileUrl(ad.image_url)} alt="" className="w-full h-32 object-cover rounded-lg" /> : <div className="w-full h-32 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 text-sm">{ad.title || "Görsel yok"}</div>}
                  <p className="text-sm text-slate-500 truncate mt-3">{ad.target_url || "URL yok"}</p>
                  <div className="flex items-center justify-between text-xs text-slate-400 mt-2"><span>{ad.impressions} gösterim</span><span>{ad.clicks} tıklama</span></div>
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center gap-2"><span className="text-sm">Aktif</span><Switch checked={ad.is_active} onCheckedChange={() => toggleAd(ad)} data-testid={`ad-toggle-${ad.id}`} /></div>
                    <Button variant="ghost" size="icon" className="text-red-500" onClick={() => delAd(ad.id)} data-testid={`ad-delete-${ad.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="settings" className="mt-6">
            <Card className="p-6 rounded-2xl max-w-lg space-y-5">
              <h2 className="font-semibold">Genel Site Ayarları</h2>
              <div><Label className="mb-1.5 block">Site Adı</Label><Input data-testid="settings-name-input" value={settings.site_name} onChange={(e) => setSettings({ ...settings, site_name: e.target.value })} /></div>
              <div><Label className="mb-1.5 block">Site Açıklaması (SEO)</Label><Input data-testid="settings-description-input" value={settings.site_description || ""} onChange={(e) => setSettings({ ...settings, site_description: e.target.value })} placeholder="Arama motorları için meta açıklama" /></div>
              <div><Label className="mb-1.5 block">Logo</Label><ImageUpload value={settings.logo_url} onChange={(v) => setSettings({ ...settings, logo_url: v })} testid="settings-logo" label="Logo" /></div>
              <div><Label className="mb-1.5 block">Favicon</Label><ImageUpload value={settings.favicon_url} onChange={(v) => setSettings({ ...settings, favicon_url: v })} testid="settings-favicon" label="Favicon" /></div>
              <div><Label className="mb-1.5 block">Geri Sayım Süresi (saniye)</Label><Input type="number" min={1} max={30} value={settings.countdown_seconds} onChange={(e) => setSettings({ ...settings, countdown_seconds: parseInt(e.target.value) || 4 })} data-testid="settings-countdown-input" /></div>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={saveSettings} data-testid="settings-save-button">Kaydet</Button>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
